/**
 * FirmaKonumHarita.jsx — firmanın harita üzerindeki konumu (Backlog #60, backend #61).
 *
 * Leaflet + react-leaflet. Projede başka harita kütüphanesi yoktu
 * (@south-paw/react-vector-maps ülke düzeyinde SVG, burada işe yaramaz) ve #61 konumu
 * GEOCODING OLMADAN, doğrudan pin bırakarak (latitude/longitude, DECIMAL(10,7)) modelledi —
 * bu yüzden salt görüntüleyen bir OSM iframe'i yetmiyordu, yazma yarısı kurulamazdı.
 *
 * ⚠️ Döşemeler (tile) dışarıdan, tile.openstreetmap.org'dan gelir: uygulamanın tek dış
 * bağımlılığı olan görsel kaynak budur. Kurumsal bir tile sunucusuna geçilecekse tek
 * değiştirilecek yer aşağıdaki TILE_URL.
 *
 * Varsayılan işaretçi ikonu KULLANILMAZ: Leaflet'in kendi PNG'leri bundler altında
 * bozuk yollarla gelir. Yerine tema renklerini taşıyan bir divIcon üretiliyor — ek
 * dosya/asset gerekmiyor.
 */
import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// Pin henüz konmamışken haritanın açılacağı yer. Fuar alanının koordinatı
// backend'de yok (fuar_alani yalnızca id + ad tutuyor, bkz. #51), bu yüzden
// ülke geneli için makul bir başlangıç: İstanbul.
const VARSAYILAN_MERKEZ = [41.0082, 28.9784];
const VARSAYILAN_ZOOM = 6;
const PIN_ZOOM = 15;

/** Tema renginde, asset gerektirmeyen damla biçimli işaretçi. */
const pinIkonu = L.divIcon({
  className: "",
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  html:
    '<div style="width:24px;height:24px;transform:translateY(-12px)">' +
    '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">' +
    '<path d="M12 1.5c-3.9 0-7 3.1-7 7 0 5.2 7 14 7 14s7-8.8 7-14c0-3.9-3.1-7-7-7z" ' +
    'fill="#f06548" stroke="#ffffff" stroke-width="1.5"/>' +
    '<circle cx="12" cy="8.5" r="2.6" fill="#ffffff"/>' +
    "</svg></div>",
});

/** Tıklanınca koordinatı yukarı bildirir (yalnızca düzenleme modunda takılır). */
function TiklamaYakalayici({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(Number(e.latlng.lat.toFixed(7)), Number(e.latlng.lng.toFixed(7)));
    },
  });
  return null;
}

/**
 * Pin dışarıdan değiştiğinde haritayı oraya taşı; ayrıca sekme/modal içinde gecikmeli
 * mount edildiğinde Leaflet'in boyutu yanlış ölçmesini düzeltir (bilinen invalidateSize
 * durumu — kapsayıcı açılırken 0 genişlikte ölçülüyor).
 */
function HaritaSenkronu({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], Math.max(map.getZoom(), PIN_ZOOM));
  }, [map, lat, lng]);
  return null;
}

/**
 * @param {number|null} lat        enlem (null = pin yok)
 * @param {number|null} lng        boylam
 * @param {boolean}     editable   true → haritaya tıklayınca onPick(lat,lng) çağrılır
 * @param {Function}    onPick     (lat, lng) => void
 * @param {number}      height     piksel yükseklik
 */
export default function FirmaKonumHarita({ lat = null, lng = null, editable = false, onPick, height = 220 }) {
  const pinVar = lat != null && lng != null;
  const merkez = useMemo(() => (pinVar ? [lat, lng] : VARSAYILAN_MERKEZ), [pinVar, lat, lng]);

  return (
    <MapContainer
      center={merkez}
      zoom={pinVar ? PIN_ZOOM : VARSAYILAN_ZOOM}
      style={{ height, width: "100%" }}
      scrollWheelZoom={editable}
      // Salt görüntüleme modunda harita bir resim gibi davranır: sayfa kaydırırken
      // yanlışlıkla yakınlaştırmasın diye etkileşim kapalı.
      dragging={editable}
      doubleClickZoom={editable}
      zoomControl={editable}
      attributionControl
    >
      <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
      <HaritaSenkronu lat={lat} lng={lng} />
      {editable && <TiklamaYakalayici onPick={onPick} />}
      {pinVar && <Marker position={[lat, lng]} icon={pinIkonu} />}
    </MapContainer>
  );
}
